/**
 * Defect Detail Component
 * Migrated from web-app-legacy/js/app/Components/Defects/Detail/
 */

import { Component, OnInit, OnDestroy, Input, Output, EventEmitter, ChangeDetectorRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';

import { TranslatePipe } from '../../pipes/translate.pipe';
import { DefectService } from '../defect.service';
import { CodebookService } from '../codebook.service';
import { DefectUserRoleService } from '../defect-user-role.service';
import { IDefect, IDefectState, ISeverityLevel, IEmployee, DefectStateEnum } from '../defect.models';
import {
  UserRole,
  formatManagerNames,
  formatManagerPhones,
  formatManagerEmails,
} from '../defect-overview/defect-overview.models';

import './defect-detail.component.scss';

// View model for defect detail
interface DefectDetailViewModel {
  id: number;
  title: string;
  description: string;
  defectState: IDefectState | null;
  severity: ISeverityLevel | null;
  category1: string | null;
  category2: string | null;
  category3: string | null;
  createdBy: string;
  createdAt: Date;
  lastUpdateAt: Date;
  contactPhone: string;
  contactEmail: string;
  imageLinks: DefectImageVm[];
  managers: IEmployee[];
  managersNames: string;
  managersPhones: string;
  managersEmails: string;
  coordinator: IEmployee | null;
  isArchived: boolean;
  couldBeReportedToSap: boolean;
  sapReportStatus: string;
  sapReportActionDisabled: boolean;
}

interface DefectImageVm {
  /** data: URL of the loaded picture, null while loading or when loading failed */
  src: string | null;
  isLoaded: boolean;
  failed: boolean;
}

@Component({
  selector: 'app-defect-detail',
  standalone: true,
  imports: [CommonModule, TranslatePipe],
  template: `
    <div class="screen screen-modal-container detail" *ngIf="defect">
      <!-- Close button -->
      <div><a class="screen-close" (click)="close()"></a></div>

      <div class="detail-container">
        <div class="detail-container-info">
          <!-- Left panel -->
          <div class="detail-container-info-left">
            <div class="detail-container-info-left-content">
              <!-- Title -->
              <div class="detail-container-info-left-content-title">
                {{ defectVm?.title }}
              </div>

              <!-- Description -->
              <div class="detail-container-info-left-content-desc">
                {{ defectVm?.description }}
              </div>

              <!-- Category 1 -->
              <div class="detail-container-info-left-content-category">
                <div class="circle">1</div>
                <div>
                  <div>{{ 'defect_detail_category1_lbl' | translate }}</div>
                  <div class="title">{{ defectVm?.category1 }}</div>
                </div>
              </div>

              <!-- Category 2 -->
              <div class="detail-container-info-left-content-category">
                <div class="circle">2</div>
                <div>
                  <div>{{ 'defect_detail_category2_lbl' | translate }}</div>
                  <div class="title">{{ defectVm?.category2 }}</div>
                </div>
              </div>

              <!-- Category 3 -->
              <div class="detail-container-info-left-content-category">
                <div class="circle">3</div>
                <div>
                  <div>{{ 'defect_detail_category3_lbl' | translate }}</div>
                  <div class="title">{{ defectVm?.category3 }}</div>
                </div>
              </div>

              <!-- Contacts section -->
              <div class="detail-container-info-left-content-contacts">
                <!-- Reporter -->
                <div class="text-block">
                  <div>
                    <span><img width="16" src="assets/images/defect/account_circle_24dp.png" /></span>
                    <span>{{ 'defect_detail_reporter_lbl' | translate }}</span>
                    <div class="person">{{ defectVm?.createdBy }}</div>
                  </div>
                </div>
                <div class="text-block">
                  <div>{{ 'defect_detail_phone_lbl' | translate }}</div>
                  <div class="contact">{{ defectVm?.contactPhone }}</div>
                </div>
                <div class="text-block">
                  <div>{{ 'defect_detail_email_lbl' | translate }}</div>
                  <div class="contact">{{ defectVm?.contactEmail }}</div>
                </div>

                <!-- Manager -->
                <div class="text-block">
                  <div>
                    <span><img width="16" src="assets/images/defect/account_circle_24dp.png" /></span>
                    <span>{{ 'defect_detail_manager_lbl' | translate }}</span>
                  </div>
                  <div class="person">{{ defectVm?.managersNames }}</div>
                </div>
                <div class="text-block">
                  <div>{{ 'defect_detail_phone_lbl' | translate }}</div>
                  <div class="contact">{{ defectVm?.managersPhones }}</div>
                </div>
                <div class="text-block">
                  <div>{{ 'defect_detail_email_lbl' | translate }}</div>
                  <div class="contact">{{ defectVm?.managersEmails }}</div>
                </div>

                <!-- Coordinator -->
                <div class="text-block">
                  <div>
                    <span><img width="16" src="assets/images/defect/account_circle_24dp.png" /></span>
                    <span>{{ 'defect_detail_coordinator_lbl' | translate }}</span>
                  </div>
                  <div class="person">{{ defectVm?.coordinator?.fullName }}</div>
                </div>
                <div class="text-block">
                  <div>{{ 'defect_detail_phone_lbl' | translate }}</div>
                  <div class="contact">{{ defectVm?.coordinator?.phone }}</div>
                </div>
                <div class="text-block">
                  <div>{{ 'defect_detail_email_lbl' | translate }}</div>
                  <div class="contact">{{ defectVm?.coordinator?.email }}</div>
                </div>
              </div>
            </div>
          </div>

          <!-- Right panel -->
          <div class="detail-container-info-right">
            <!-- Tools -->
            <div class="detail-container-info-right-tools">
              <div class="pdf">
                <a *ngIf="!exportInProgress" (click)="exportPdf()">
                  <img src="assets/images/defect/pdf-white.png" />
                </a>
                <div class="spinner" *ngIf="exportInProgress"></div>
              </div>
              <div class="edit" [class.disabled]="!isEditable">
                <a (click)="editDefect()">
                  <img *ngIf="isEditable" src="assets/images/defect/pencil-outline.png" />
                  <img *ngIf="!isEditable" src="assets/images/defect/block_white.png" />
                </a>
              </div>
            </div>

            <!-- Content -->
            <div class="detail-container-info-right-content">
              <div class="detail-status-info">
                <div class="text-block">
                  <div>Id: {{ defectVm?.id }}</div>
                  <div class="text-block-value">
                    <span><img width="16" src="assets/images/defect/outline_star_black.png" /></span>
                    {{ formatDate(defectVm?.createdAt) }}
                  </div>
                  <div class="text-block-value">
                    <span><img width="16" src="assets/images/defect/edit_black.png" /></span>
                    {{ formatDate(defectVm?.lastUpdateAt) }}
                  </div>
                </div>
                <div class="text-block">
                  <div>{{ 'defect_detail_severity_lbl' | translate }}</div>
                  <div class="text-block-value">
                    <span class="severity-icon" [ngClass]="getSeverityIconClass(defectVm?.severity?.id)"></span>
                    {{ defectVm?.severity?.title }}
                  </div>
                </div>
                <div class="text-block">
                  <div>{{ 'defect_detail_state_lbl' | translate }}</div>
                  <div class="text-block-value">
                    <img [src]="getStateIcon(defectVm?.defectState?.id)" />
                    {{ defectVm?.defectState?.title }}
                  </div>
                </div>
              </div>

              <!-- SAP section -->
              <div class="detail-status-info" *ngIf="defectVm?.couldBeReportedToSap">
                <div class="text-block">
                  <div>Stav odeslání závady</div>
                  <div class="text-block-value">
                    <div class="contact">{{ defectVm?.sapReportStatus }}</div>
                  </div>
                </div>
                <div class="text-block">
                  <button
                    class="link-btn"
                    (click)="reportToSap()"
                    [disabled]="defectVm?.sapReportActionDisabled"
                  >
                    Odeslat do SAP
                  </button>
                </div>
              </div>

              <!-- Image gallery -->
              <div class="detail-container-images">
                <div *ngFor="let link of defectVm?.imageLinks" class="detail-container-images-item">
                  <div class="bounce-spinner" *ngIf="!link.src && !link.failed">
                    <div class="bounce1"></div>
                    <div class="bounce2"></div>
                    <div class="bounce3"></div>
                  </div>
                  <div *ngIf="link.src">
                    <img [src]="link.src" (load)="link.isLoaded = true" />
                    <div class="detail-container-images-item-show">
                      <a (click)="showFullImage(link)">
                        <img src="assets/images/defect/magnify-plus-white.png" />
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- Fullscreen image overlay -->
    <div class="defect-detail-full-image-container" *ngIf="fullImageVisible">
      <div class="defect-detail-full-image-container-close">
        <a (click)="closeFullImage()"></a>
      </div>
      <div class="defect-detail-full-image-container-wrapper">
        <img [src]="selectedImage?.src" />
      </div>
    </div>
  `,
})
export class DefectDetailComponent implements OnInit {
  @Input() defect: IDefect | null = null;
  @Output() closeEvent = new EventEmitter<void>();
  @Output() editDefectEvent = new EventEmitter<IDefect>();

  private defectService = inject(DefectService);
  private codebookService = inject(CodebookService);
  private userRoleService = inject(DefectUserRoleService);
  private cdr = inject(ChangeDetectorRef);

  defectVm: DefectDetailViewModel | null = null;
  fullImageVisible = false;
  selectedImage: DefectImageVm | null = null;
  exportInProgress = false;
  isEditable = false;

  ngOnInit(): void {
    if (this.defect) {
      this.initDefectViewModel();
    }
  }

  private initDefectViewModel(): void {
    if (!this.defect) {
      return;
    }

    const defectState = this.codebookService.getDefectStateById(this.defect.defectStateId);
    const severity = this.codebookService.getSeverityById(this.defect.severityLevelId);
    const category1 = this.codebookService.getCategory1ById(this.defect.category1Id);
    const category2 = this.codebookService.getCategory2ById(this.defect.category2Id);
    const category3 = this.codebookService.getCategory3ById(this.defect.category3Id);

    const isArchived =
      this.defect.defectStateId === DefectStateEnum.Solved ||
      this.defect.defectStateId === DefectStateEnum.Refused;

    const sapIntegrationEnabled = this.userRoleService.isSapIntegrationEnabled();

    // Determine SAP report status text
    let sapReportStatus = '';
    switch (this.defect.reportToSapStatus) {
      case 0:
        sapReportStatus = 'Neodesláno';
        break;
      case 1:
        sapReportStatus = 'Odesílání';
        break;
      case 2:
        sapReportStatus = 'Odesláno';
        break;
      case 3:
        sapReportStatus = 'Odesláno s chybou';
        break;
    }

    this.defectVm = {
      id: this.defect.id,
      title: this.defect.title,
      description: this.defect.description,
      defectState,
      severity,
      category1: category1?.title ?? null,
      category2: category2?.title ?? null,
      category3: category3?.title ?? null,
      createdBy: this.defect.createdBy,
      createdAt: new Date(this.defect.createdAt * 1000),
      lastUpdateAt:
        this.defect.lastUpdateAt > 0
          ? new Date(this.defect.lastUpdateAt * 1000)
          : new Date(this.defect.createdAt * 1000),
      contactEmail: this.defect.contactEmail,
      contactPhone: this.defect.contactPhone,
      managers: this.defect.managers,
      managersNames: formatManagerNames(this.defect.managers),
      managersPhones: formatManagerPhones(this.defect.managers),
      managersEmails: formatManagerEmails(this.defect.managers),
      coordinator: this.defect.coordinator,
      isArchived,
      couldBeReportedToSap: this.defect.couldBeReportedToSap && sapIntegrationEnabled,
      sapReportStatus,
      sapReportActionDisabled:
        this.defect.reportToSapStatus === 1 || this.defect.reportToSapStatus === 2,
      imageLinks: [],
    };

    this.isEditable = this.userRoleService.canEditDefect(this.defect) && !isArchived;

    // Load images. The picture data is fetched through HttpClient (with the
    // Bearer token) and shown from a data: URL.
    this.defectService.getDefectImages(this.defect.id).subscribe({
      next: (pictureList) => {
        const vm = this.defectVm;
        if (!pictureList.items || !vm) {
          return;
        }
        vm.imageLinks = pictureList.items.map((): DefectImageVm => ({ src: null, isLoaded: false, failed: false }));
        pictureList.items.forEach((img, i) => {
          const link = vm.imageLinks[i];
          this.defectService.getImageDataUrl(img.id).subscribe({
            next: (url) => {
              link.src = url;
              this.cdr.detectChanges();
            },
            error: (err) => {
              console.error('[DefectDetail] Error loading picture:', img.id, err);
              link.failed = true;
              this.cdr.detectChanges();
            },
          });
        });
        this.cdr.detectChanges();
      },
    });
  }

  formatDate(date: Date | undefined): string {
    if (!date) {
      return '';
    }
    const d = new Date(date);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    const seconds = String(d.getSeconds()).padStart(2, '0');
    return `${day}.${month}.${year} ${hours}:${minutes}:${seconds}`;
  }

  getSeverityIconClass(severityId: number | undefined): string {
    switch (severityId) {
      case 1:
        return 'severity-high';
      case 2:
        return 'severity-medium';
      case 3:
        return 'severity-low';
      default:
        return '';
    }
  }

  getStateIcon(stateId: number | undefined): string {
    switch (stateId) {
      case 1:
        return 'assets/images/defect/reported-black.png';
      case 2:
        return 'assets/images/defect/inprogress-black.png';
      case 3:
        return 'assets/images/defect/solved-green.png';
      case 4:
        return 'assets/images/defect/planned-black.png';
      default:
        return 'assets/images/defect/reported-black.png';
    }
  }

  showFullImage(image: DefectImageVm): void {
    this.fullImageVisible = true;
    this.selectedImage = image;
  }

  closeFullImage(): void {
    this.fullImageVisible = false;
    this.selectedImage = null;
  }

  exportPdf(): void {
    if (!this.defect) {
      return;
    }

    this.exportInProgress = true;
    this.defectService.exportDefectPdf(this.defect.id).subscribe({
      next: ({ blob, filename }) => {
        this.defectService.downloadFile(blob, filename);
        this.exportInProgress = false;
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('[DefectDetail] Error exporting PDF:', error);
        this.exportInProgress = false;
        this.cdr.detectChanges();
      },
    });
  }

  reportToSap(): void {
    if (!this.defect || !this.defectVm) {
      return;
    }

    this.defectService.reportToSap(this.defect.id).subscribe({
      next: () => {
        this.defectVm!.sapReportActionDisabled = true;
        this.defectVm!.sapReportStatus = 'Odesílání';
        this.cdr.detectChanges();
      },
      error: (error) => {
        console.error('[DefectDetail] Error reporting to SAP:', error);
      },
    });
  }

  editDefect(): void {
    if (!this.isEditable || !this.defect) {
      return;
    }
    this.editDefectEvent.emit(this.defect);
  }

  close(): void {
    this.closeEvent.emit();
  }
}
